import type { GpsSample, SwerveEvent } from './types';

/**
 * Detect swerves — lateral spike where the driver deviates from their
 * line and then RETURNS to roughly the original bearing. The return
 * is what distinguishes a swerve from a normal turn at a junction or
 * roundabout (which leaves the driver pointing somewhere new).
 *
 *   ─────────►──┐         ─────────►──┐
 *               │                     ╲
 *               ▼  ←  turn             ╲___►───  ←  swerve
 *                                              (returns to original line)
 *
 * Pipeline:
 *   1. Watch lateral acceleration. When it spikes above the threshold
 *      AND the recent heading delta is significant, register a
 *      "pending swerve" snapshot: pre-spike bearing, peak time, peak
 *      lateral magnitude + sign.
 *   2. Wait `returnWindowMs` after the peak.
 *   3. Sample the current bearing. If it's within `returnToleranceDeg`
 *      of the pre-spike bearing → emit a swerve event. Otherwise
 *      discard (it was a turn).
 *
 * Tunables (defaults match SPEC § Swerve detection):
 *   speedGateKmh = 25
 *   lateralThresholdMs2 = 3
 *   headingDeltaDeg = 15            (peak excursion required)
 *   peakWindowMs = 1500              (window the heading delta is measured over)
 *   returnWindowMs = 2500            (wait this long after peak before checking return)
 *   returnToleranceDeg = 20          (post-bearing must be within this of pre-bearing)
 *   refractoryMs = 3000
 */
export interface SwerveDetectorOpts {
  speedGateKmh?: number;
  lateralThresholdMs2?: number;
  headingDeltaDeg?: number;
  peakWindowMs?: number;
  returnWindowMs?: number;
  returnToleranceDeg?: number;
  refractoryMs?: number;
  deviceClass: string;
}

interface CourseSample {
  course: number;
  ts: number;
}

interface PendingSwerve {
  preBearing: number;
  peakTs: number;
  peakLatMag: number;
  peakLatSign: number; // +1 = right, -1 = left (after rotation by GravityEstimator + ax sign)
  lat: number;
  lon: number;
  speedKmh: number;
}

/** Smallest signed angular difference, normalised to [-180, +180]. */
function angleDelta(a: number, b: number): number {
  return ((a - b + 540) % 360) - 180;
}

export class SwerveDetector {
  private readonly speedGateKmh: number;
  private readonly lateralThresholdMs2: number;
  private readonly headingDeltaDeg: number;
  private readonly peakWindowMs: number;
  private readonly returnWindowMs: number;
  private readonly returnToleranceDeg: number;
  private readonly refractoryMs: number;
  private readonly deviceClass: string;

  // Course buffer needs to cover (peakWindowMs + returnWindowMs) of history
  // so we can look back AND look forward.
  private courseBuf: CourseSample[] = [];
  private bufWindowMs: number;
  private lastFireTs = 0;
  private lastGps: GpsSample | null = null;
  private pending: PendingSwerve | null = null;

  constructor(opts: SwerveDetectorOpts) {
    this.speedGateKmh = opts.speedGateKmh ?? 25;
    this.lateralThresholdMs2 = opts.lateralThresholdMs2 ?? 3;
    this.headingDeltaDeg = opts.headingDeltaDeg ?? 15;
    this.peakWindowMs = opts.peakWindowMs ?? 1500;
    this.returnWindowMs = opts.returnWindowMs ?? 2500;
    this.returnToleranceDeg = opts.returnToleranceDeg ?? 20;
    this.refractoryMs = opts.refractoryMs ?? 3000;
    this.deviceClass = opts.deviceClass;
    this.bufWindowMs = this.peakWindowMs + this.returnWindowMs + 1000;
  }

  setGps(s: GpsSample) {
    this.lastGps = s;
    if (s.courseDeg !== null) {
      this.courseBuf.push({ course: s.courseDeg, ts: s.ts });
      while (
        this.courseBuf.length > 0 &&
        s.ts - this.courseBuf[0].ts > this.bufWindowMs
      ) {
        this.courseBuf.shift();
      }
    }
  }

  /**
   * Feed lateral acceleration (m/s², gravity-removed) plus a SIGNED
   * variant (positive = right, negative = left). The magnitude is
   * what crosses the threshold; the sign is recorded for the swerve
   * direction field.
   */
  feed(aLateralMag: number, aLateralSigned: number, ts: number): SwerveEvent | null {
    const gps = this.lastGps;

    // First: check whether a pending swerve has reached the return-check
    // window. This runs regardless of whether THIS sample is a spike.
    const matured = this.pending && ts - this.pending.peakTs >= this.returnWindowMs;
    let emit: SwerveEvent | null = null;
    if (matured && this.pending) {
      emit = this.resolvePending(ts);
    }

    if (!gps || gps.speedKmh < this.speedGateKmh) return emit;
    if (ts - this.lastFireTs < this.refractoryMs) return emit;
    if (gps.courseDeg === null) return emit;
    if (this.courseBuf.length < 2) return emit;

    if (aLateralMag < this.lateralThresholdMs2) return emit;

    // Look back over peakWindowMs to find the heading excursion.
    const peakWindowStart = ts - this.peakWindowMs;
    const recent = this.courseBuf.filter((s) => s.ts >= peakWindowStart);
    if (recent.length < 2) return emit;
    const startBearing = recent[0].course;
    let maxDelta = 0;
    for (const s of recent) {
      const d = Math.abs(angleDelta(s.course, startBearing));
      if (d > maxDelta) maxDelta = d;
    }
    if (maxDelta < this.headingDeltaDeg) return emit;

    // Either start a new pending or update the existing one with a
    // larger peak. Pre-bearing comes from the OLDEST sample in the
    // peak-window slice (just before the manoeuvre began).
    if (!this.pending) {
      this.pending = {
        preBearing: startBearing,
        peakTs: ts,
        peakLatMag: aLateralMag,
        peakLatSign: Math.sign(aLateralSigned) || 1,
        lat: gps.lat,
        lon: gps.lon,
        speedKmh: gps.speedKmh,
      };
    } else if (aLateralMag > this.pending.peakLatMag) {
      // Update peak with the larger spike — keeps location + bearing
      // anchored to where the most violent deflection happened.
      this.pending.peakTs = ts;
      this.pending.peakLatMag = aLateralMag;
      this.pending.peakLatSign = Math.sign(aLateralSigned) || 1;
      this.pending.lat = gps.lat;
      this.pending.lon = gps.lon;
      this.pending.speedKmh = gps.speedKmh;
    }

    return emit;
  }

  /**
   * Decide whether a pending swerve was real (driver returned to
   * original bearing) or a turn (driver stayed on the new bearing).
   * Emit on real swerve, discard otherwise.
   */
  private resolvePending(ts: number): SwerveEvent | null {
    const p = this.pending;
    if (!p) return null;
    this.pending = null;

    // Current bearing from the freshest course sample
    if (this.courseBuf.length === 0) return null;
    const currentBearing = this.courseBuf[this.courseBuf.length - 1].course;
    const drift = Math.abs(angleDelta(currentBearing, p.preBearing));
    if (drift > this.returnToleranceDeg) {
      // It was a turn, not a swerve — driver is pointing somewhere new.
      return null;
    }

    // Real swerve. Use the peak-window heading excursion as the
    // headingDeltaDeg payload (peak magnitude, not net change).
    const peakWindowStart = p.peakTs - this.peakWindowMs;
    const slice = this.courseBuf.filter((s) => s.ts >= peakWindowStart && s.ts <= p.peakTs);
    let peakExcursion = 0;
    for (const s of slice) {
      const d = Math.abs(angleDelta(s.course, p.preBearing));
      if (d > peakExcursion) peakExcursion = d;
    }

    this.lastFireTs = ts;
    return {
      kind: 'swerve',
      lat: p.lat,
      lon: p.lon,
      speedKmh: p.speedKmh,
      peakLatG: p.peakLatMag,
      headingDeltaDeg: peakExcursion,
      direction: p.peakLatSign >= 0 ? 'right' : 'left',
      confidence: 0.2,
      deviceClass: this.deviceClass,
      ts: p.peakTs,
    };
  }
}
